import imageData from './brand-image-map.json'
import routes from './brandRoutes.json'

export type BrandScene = {
  brandId: number
  menuId?: number
  title: string
  filename: string
  src: string
  alt: string
  width: number
  height: number
  logo: string
  logoEmbedded: boolean
  logoOverlay: boolean
  aiGenerated: boolean
  sourceType: 'catalog-original' | 'catalog-scene' | 'brand-logo'
  sourceProductImage?: string
  productId?: number
  sourceShop?: number
  unavailableReason?: string
  directMenu?: boolean
}

const allScenes: Record<string, BrandScene> = imageData as Record<string, BrandScene>
export const brandScenes: Record<string, BrandScene> = Object.fromEntries(
  Object.entries(allScenes).filter(([key]) => !key.startsWith('direct-menu-')),
)
export const directBrandScenes: Record<number, BrandScene> = Object.fromEntries(
  Object.values(allScenes).filter(scene => scene.directMenu && scene.menuId)
    .map(scene => [scene.menuId!, scene]),
)
const brandRoutes: Record<string, { title: string }> = routes
const sceneWidths = [360, 480, 640, 768, 960, 1024, 1200, 1280, 1600, 1920]

export const brandSceneKey = (brandId: number, menuId?: number) =>
  menuId ? `brand-menu-${brandId}-${menuId}` : `brand-${brandId}`

// Catalog scenes retain the exact original product reference separately from
// their AI-assisted setting. When no suitable product exists, use its brand's
// original logo instead. Direct menus retain their own reviewed scene.
export const getBrandScene = (brandId: number, menuId?: number) =>
  brandScenes[brandSceneKey(brandId, menuId)]

export const getDirectBrandScene = (menuId: number) => directBrandScenes[menuId]

const presentations = new Map(Object.values(allScenes).map(scene => [scene.src, {
  brandId: scene.brandId,
  productId: scene.productId,
  ...(scene.sourceType === 'catalog-scene' ? {
    srcSet: sceneWidths
      .filter((width, index, widths) => index === 0 || Math.min(width, scene.width) !== Math.min(widths[index - 1], scene.width))
      .map(width => `${scene.src.replace(/-w\d+\.webp$/, `-w${width}.webp`)} ${Math.min(width, scene.width)}w`).join(', '),
  } : {}),
  width: scene.width,
  height: scene.height,
  aiGenerated: scene.aiGenerated,
  logoSrc: scene.logo,
  logoAlt: `Logo von ${brandRoutes[String(scene.brandId)]?.title || scene.title}`,
  logoEmbedded: scene.logoEmbedded,
  logoOverlay: scene.logoOverlay,
}]))

export const getBrandImagePresentation = (src: string) => {
  let imagePath = src
  if (/^https?:\/\//.test(src)) {
    try {
      const url = new URL(src)
      if (!['homeylove.de', 'www.homeylove.de'].includes(url.hostname)) return undefined
      imagePath = url.pathname
    } catch { return undefined }
  }
  return presentations.get(imagePath)
}
